<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
/* global axios */

const props = defineProps({
  pipelineId: { type: Number, required: true },
  accountId: { type: [String, Number], required: true },
  // Pipeline padre · si se pasa, se muestra info de sincronización con sucursales
  pipeline: { type: Object, default: null },
});

const emit = defineEmits(['close', 'created']);

const store = useStore();

const PRESET_COLORS = [
  '#3b82f6', '#06b6d4', '#10b981', '#eab308', '#f97316',
  '#ef4444', '#ec4899', '#a855f7', '#14b8a6', '#84cc16',
];

// Info de sincronización · stages heredan scope del pipeline padre
const allBranches = computed(() => store.getters['branches/getBranches'] || []);

// Sucursales disponibles para la etapa: las del scope del pipeline (o todas si pipeline es global)
const availableBranches = computed(() => {
  const pipelineScope = props.pipeline?.scope_branch_ids || [];
  if (pipelineScope.length === 0) return allBranches.value;
  return allBranches.value.filter(b => pipelineScope.includes(b.id));
});

// Modo branch scope: 'all' = heredar del pipeline · 'specific' = lista custom
const branchMode = ref('all');
const selectedBranchIds = ref([]);

function toggleBranch(branchId) {
  const idx = selectedBranchIds.value.indexOf(branchId);
  if (idx >= 0) selectedBranchIds.value.splice(idx, 1);
  else selectedBranchIds.value.push(branchId);
}

const syncInfo = computed(() => {
  if (!props.pipeline) return null;

  // Modo "específicas": muestra info según selección del usuario
  if (branchMode.value === 'specific') {
    const selected = selectedBranchIds.value;
    if (selected.length === 0) {
      return {
        type: 'warn',
        text: '⚠ Selecciona al menos una sucursal · si no, la etapa no aparecerá en ninguna.',
      };
    }
    const selectedNames = availableBranches.value.filter(b => selected.includes(b.id)).map(b => b.name);
    const excludedNames = availableBranches.value.filter(b => !selected.includes(b.id)).map(b => b.name);
    let text = `🏢 Esta columna se mostrará SOLO en ${selected.length} sucursal${selected.length > 1 ? 'es' : ''}: ${selectedNames.join(', ')}.`;
    if (excludedNames.length > 0) {
      text += ` En ${excludedNames.join(', ')} NO aparecerá.`;
    }
    return { type: 'specific', text };
  }

  // Modo "todas": comportamiento original (heredar del pipeline)
  const scope = props.pipeline.scope_branch_ids || [];
  if (scope.length === 0) {
    return {
      type: 'global',
      text: `🌐 Esta columna se agregará automáticamente al embudo "${props.pipeline.name}", que está activo en TODAS las ${allBranches.value.length} sucursales.`,
    };
  }
  const names = allBranches.value.filter(b => scope.includes(b.id)).map(b => b.name);
  return {
    type: 'specific',
    text: `🏢 Esta columna se agregará al embudo "${props.pipeline.name}" en ${scope.length} sucursal${scope.length > 1 ? 'es' : ''}: ${names.join(', ')}.`,
  };
});

const name = ref('');
const color = ref('#3b82f6');
const stageType = ref('open');
const saving = ref(false);
const errorMsg = ref(null);

async function save() {
  if (!name.value) return;
  if (branchMode.value === 'specific' && selectedBranchIds.value.length === 0) {
    errorMsg.value = 'Selecciona al menos una sucursal o cambia a "Todas las sucursales del embudo"';
    return;
  }
  saving.value = true;
  errorMsg.value = null;
  try {
    const stagePayload = {
      name: name.value,
      color: color.value,
      stage_type: stageType.value,
    };
    if (branchMode.value === 'specific') {
      stagePayload.scope_branch_ids = [...selectedBranchIds.value];
    }
    const { data } = await axios.post(
      `/api/v1/accounts/${props.accountId}/pipelines/${props.pipelineId}/stages`,
      { stage: stagePayload }
    );
    emit('created', data);
  } catch (e) {
    errorMsg.value = e.response?.data?.message || e.message;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="nsd-overlay" @click.self="emit('close')">
    <div class="nsd-modal" role="dialog" aria-modal="true">
      <div class="nsd-head">
        <div class="nsd-title-wrap">
          <div class="nsd-title-icon">📋</div>
          <div>
            <div class="nsd-title">Nueva columna</div>
            <div class="nsd-subtitle">Etapa del embudo · sincroniza con sucursales del pipeline</div>
          </div>
        </div>
        <button class="nsd-close" type="button" @click="emit('close')" aria-label="Cerrar">×</button>
      </div>

      <div class="nsd-body">
        <div class="nsd-field-label">Nombre</div>
        <input v-model="name" placeholder="Ej: ENVIADO A IMPRENTA" class="nsd-input" />

        <div class="nsd-section">
          <div class="nsd-field-label">Color</div>
          <div class="nsd-color-grid">
            <button
              v-for="c in PRESET_COLORS"
              :key="c"
              @click="color = c"
              type="button"
              class="nsd-color-swatch"
              :class="{ 'is-on': color === c }"
              :style="{ background: c }"
            />
            <input v-model="color" type="color" class="nsd-color-custom" />
          </div>
        </div>

        <div class="nsd-section">
          <div class="nsd-field-label">Tipo de etapa</div>
          <select v-model="stageType" class="nsd-input nsd-select">
            <option value="open">Abierto</option>
            <option value="won">Logrado</option>
            <option value="lost">Perdido</option>
          </select>
        </div>

        <!-- Sucursales (NUEVO) · permite limitar la etapa a sucursales específicas -->
        <div class="nsd-section">
          <div class="nsd-field-label">Sucursales</div>
          <div class="nsd-branch-mode">
            <button
              type="button"
              class="nsd-branch-mode-btn"
              :class="{ active: branchMode === 'all' }"
              @click="branchMode = 'all'"
            >🌐 Todas las del embudo</button>
            <button
              type="button"
              class="nsd-branch-mode-btn"
              :class="{ active: branchMode === 'specific' }"
              @click="branchMode = 'specific'"
            >🏢 Solo algunas específicas</button>
          </div>

          <div v-if="branchMode === 'specific'" class="nsd-branch-list">
            <button
              v-for="b in availableBranches"
              :key="b.id"
              type="button"
              class="nsd-branch-item"
              :class="{ selected: selectedBranchIds.includes(b.id) }"
              @click="toggleBranch(b.id)"
            >
              <span class="nsd-branch-checkbox"></span>
              <span class="nsd-branch-info">
                <span class="nsd-branch-name">📷 {{ b.name }}</span>
                <span class="nsd-branch-code">{{ b.email || `Sucursal #${b.id}` }}</span>
              </span>
            </button>
            <div v-if="availableBranches.length === 0" class="nsd-branch-empty">
              Sin sucursales en este embudo
            </div>
          </div>
        </div>

        <!-- Info de sincronización con sucursales -->
        <div v-if="syncInfo" class="nsd-sync-info" :class="syncInfo.type">
          {{ syncInfo.text }}
        </div>

        <div v-if="errorMsg" class="nsd-error">⚠ {{ errorMsg }}</div>
      </div>

      <div class="nsd-foot">
        <button @click="emit('close')" class="nsd-btn nsd-cancel" type="button">Cancelar</button>
        <button @click="save" :disabled="saving || !name" class="nsd-btn nsd-save" type="button">
          {{ saving ? '⏳ Creando…' : '⚡ Crear columna' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style>
/* NewStageDialog · M1.B */
.nsd-overlay { position: fixed; inset: 0; z-index: 50; background: rgba(0,0,0,0.78); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; padding: 24px; font-family: -apple-system, system-ui, sans-serif; }
.nsd-modal { width: 460px; max-width: 100%; background: rgba(10,10,10,0.98); border: 1.5px solid rgba(251,191,36,0.45); border-radius: 18px; overflow: hidden; box-shadow: 0 30px 90px rgba(0,0,0,0.85), 0 0 50px rgba(251,191,36,0.18); position: relative; color: #fff; }
.nsd-modal::before { content: ''; position: absolute; inset: 0; pointer-events: none; border-radius: 18px; background: linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.04) 22%, transparent 24%), radial-gradient(circle at 100% 0%, rgba(251,191,36,0.10), transparent 60%); z-index: 1; }
.nsd-modal > * { position: relative; z-index: 2; }

.nsd-head { padding: 16px 20px; border-bottom: 1px solid rgba(255,255,255,0.06); display: flex; justify-content: space-between; align-items: center; background: radial-gradient(ellipse at 0% 0%, rgba(251,191,36,0.10), transparent 60%); }
.nsd-title-wrap { display: flex; align-items: center; gap: 10px; }
.nsd-title-icon { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(217,119,6,0.10)); border: 1.5px solid rgba(251,191,36,0.40); display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
.nsd-title { font-size: 16px; font-weight: 900; color: #fff; line-height: 1.15; }
.nsd-subtitle { font-size: 10px; color: rgba(255,255,255,0.55); margin-top: 1px; }
.nsd-close { width: 26px; height: 26px; border-radius: 50%; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; line-height: 1; padding: 0; font-family: inherit; transition: all 0.15s; }
.nsd-close:hover { background: rgba(239,68,68,0.20); color: #fff; border-color: rgba(239,68,68,0.40); transform: rotate(90deg); }

.nsd-body { padding: 16px 20px; }
.nsd-field-label { font-size: 10px; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(255,255,255,0.55); font-weight: 800; margin-bottom: 6px; }
.nsd-input { width: 100%; padding: 9px 12px; background: rgba(255,255,255,0.04); border: 1px solid rgba(251,191,36,0.30); border-radius: 7px; color: #fff; font-size: 13px; font-family: inherit; outline: none; }
.nsd-input:focus { border-color: #fbbf24; box-shadow: 0 0 0 3px rgba(251,191,36,0.15); }
.nsd-select option { background: #0a0a0a; color: #fff; }
.nsd-section { margin-top: 14px; }

.nsd-color-grid { display: grid; grid-template-columns: repeat(8, 1fr); gap: 6px; }
.nsd-color-swatch { width: 100%; aspect-ratio: 1; border-radius: 8px; border: 2px solid transparent; cursor: pointer; padding: 0; }
.nsd-color-swatch.is-on { border-color: #fff; box-shadow: 0 0 12px rgba(251,191,36,0.45); transform: scale(1.05); }
.nsd-color-custom { width: 100%; aspect-ratio: 1; border-radius: 8px; cursor: pointer; border: 1px solid rgba(255,255,255,0.18); background: transparent; padding: 0; }

.nsd-sync-info { margin-top: 14px; padding: 10px 12px; border-radius: 8px; font-size: 11px; line-height: 1.5; }
.nsd-sync-info.global { background: rgba(34,197,94,0.06); border: 1px dashed rgba(34,197,94,0.40); color: #4ade80; }
.nsd-sync-info.specific { background: rgba(99,102,241,0.06); border: 1px dashed rgba(99,102,241,0.40); color: #a5b4fc; }
.nsd-sync-info.warn { background: rgba(239,68,68,0.06); border: 1px dashed rgba(239,68,68,0.40); color: #fca5a5; }

/* Branch mode toggle + lista (NUEVO Fase 3b · per-stage scope) */
.nsd-branch-mode {
  display: flex; gap: 6px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 9px;
  padding: 3px;
  margin-bottom: 8px;
}
.nsd-branch-mode-btn {
  flex: 1;
  padding: 7px 10px;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: rgba(255,255,255,0.65);
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.nsd-branch-mode-btn:hover { color: #fff; }
.nsd-branch-mode-btn.active {
  background: rgba(251,191,36,0.18);
  color: #fbbf24;
  box-shadow: inset 0 0 0 1px rgba(251,191,36,0.30);
}

.nsd-branch-list {
  display: flex; flex-direction: column;
  gap: 4px;
  margin-top: 6px;
  max-height: 220px;
  overflow-y: auto;
}
.nsd-branch-item {
  display: flex; align-items: center; gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: all 0.15s;
}
.nsd-branch-item:hover { border-color: rgba(251,191,36,0.25); }
.nsd-branch-item.selected {
  background: rgba(251,191,36,0.10);
  border-color: rgba(251,191,36,0.40);
}
.nsd-branch-checkbox {
  width: 18px; height: 18px;
  border-radius: 5px;
  border: 1.5px solid rgba(255,255,255,0.30);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 900;
  color: transparent;
}
.nsd-branch-item.selected .nsd-branch-checkbox {
  background: #fbbf24;
  border-color: #fbbf24;
  color: #1a1209;
}
.nsd-branch-item.selected .nsd-branch-checkbox::before { content: '✓'; }
.nsd-branch-info { flex: 1; line-height: 1.25; min-width: 0; }
.nsd-branch-name { display: block; font-size: 12px; font-weight: 800; color: #fff; }
.nsd-branch-code { display: block; font-size: 10px; color: rgba(255,255,255,0.55); margin-top: 1px; }
.nsd-branch-empty {
  padding: 14px;
  text-align: center;
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  border: 1px dashed rgba(255,255,255,0.10);
  border-radius: 8px;
}

.nsd-error { margin-top: 12px; padding: 10px 12px; background: rgba(239,68,68,0.10); border: 1px solid rgba(239,68,68,0.40); border-radius: 8px; font-size: 11px; color: #f87171; font-weight: 700; }

.nsd-foot { padding: 12px 20px; border-top: 1px solid rgba(255,255,255,0.06); display: flex; justify-content: flex-end; gap: 8px; background: rgba(0,0,0,0.4); }
.nsd-btn { padding: 9px 16px; border-radius: 8px; font-size: 12px; font-weight: 800; cursor: pointer; border: none; font-family: inherit; }
.nsd-btn:disabled { opacity: 0.5; cursor: wait; }
.nsd-cancel { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.65); border: 1px solid rgba(255,255,255,0.08); }
.nsd-cancel:hover { background: rgba(255,255,255,0.08); }
.nsd-save { background: linear-gradient(135deg, #fbbf24, #d97706); color: #000; box-shadow: 0 0 14px rgba(251,191,36,0.30); }
.nsd-save:hover:not(:disabled) { box-shadow: 0 0 22px rgba(251,191,36,0.55); }
</style>
